import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Upload, Space, message, Row, Col, Divider, Tag, Alert } from 'antd';
import { UploadOutlined, CloudUploadOutlined, ReloadOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo } from '../utils/channelLogos';
import { metaApi, testApi } from '../services/api';

const { Dragger } = Upload;

// 渠道列表动态获取（见组件内 loadChannels）：与开户/充值/提现页同源，
// CHANNEL_CURRENCIES → 中台 /v1/fiat/payment/channel/config/channel-currencies
// → fiat_channel_config（status=1 且在有效期内），并已按登录用户的 channel/currencies 过滤。
// 下面的常量不充当「渠道列表」，只做排序、打标、场景限制与接口失败时的兜底。

// 本接口对所有渠道都只是「落库」：存中台 S3 + 写 channel_file_meta，拿到 fileId。
// 真正把文件交给渠道，发生在后续的开户 / KYC / 出金流程里 —— 渠道那时才有可挂载的主体 ID
// （Account Request / subaccount / entity / 出金单）。
//
// 注：Tazapay 的 uploader 在异步分发时确实会调一次渠道（pre-signed URL + PUT），
// 但那次的产物（Tazapay fileName，落在 channel_file_meta.channel_file_id）后续没有任何地方读 ——
// 建用户与补件走 TazapayUserChannelService.uploadSingleFile，拿中台 S3 地址重新上传一遍再 addDocument。
// 所以从使用者视角，Tazapay 同样是「只落库」，不要以为传完就挂到渠道实体上了。
// 下面这份清单只用于排序与接口失败时兜底，不代表渠道行为有差别。
const KNOWN_CHANNELS = ['TAZAPAY', 'PYVIO', 'MDAQ', 'MOOLAHGO', 'AVENIA', 'GOWD', 'FV_BANK', 'DLOCAL'];

// channel-currencies 拿不到时的兜底，避免渠道下拉直接空掉
const FALLBACK_CHANNELS = KNOWN_CHANNELS;

// 渠道 → 允许的 scene。中台会按 scene 卡：GOWD 开户时校验附件
// scene ∈ {ACCOUNT_OPEN, USER_CREATE}（GowdAttachmentSourceResolver），
// FV Bank 本期只做出金附件。未列出的渠道不限制。
const CHANNEL_SCENES = {
  GOWD: ['ACCOUNT_OPEN', 'USER_CREATE'],
  FV_BANK: ['WITHDRAW'],
  DLOCAL: ['USER_CREATE'],
};

const CHANNEL_HINTS = {
  TAZAPAY: 'Tazapay: 上传阶段只落库（中台 S3 + channel_file_meta）。分发线程虽然会调一次渠道 pre-signed URL + PUT，'
    + '但那次的 fileName 后续没人用 —— 建用户/补件会拿中台 S3 地址重新上传一遍再 addDocument 挂到 entity。'
    + '支持 KYC/Entity 文档；注意本页 Tazapay 的文件类型用的是渠道原文（proof_of_identity 等），与其它渠道的平台统一类型不是一套词表。',
  PYVIO: 'Pyvio: 上传阶段只落库（中台 S3 + channel_file_meta），真正推给 Pyvio 发生在开户/绑卡流程里（绑卡会读回 channel_file_id）。支持 KYC/EDD 文件类型。',
  MDAQ: 'MDAQ (Wallex): 上传阶段只落库，真正调 MDAQ document 接口并 screen 的是创建用户流程（uploadFilesToWallex）。支持公司/身份/地址证明文件。',
  MOOLAHGO: 'MoolahGo: 上传阶段只落库，真正推送发生在 KYC 创建流程（init 拿到 user_id 之后）；已推送的附件按 channel_file_id 非空做幂等跳过。',
  AVENIA: 'Avenia: 上传阶段只落库 —— subaccount 还没创建，渠道侧无处挂载文档。真正推送在开户流程里。',
  GOWD: 'GOWD: 上传阶段只落库 —— 文档必须挂在 accountRequestId 之下，而它要到开户时才产生，真正推送发生在开户/补件流程。'
    + '三条硬约束：scene 必须是 ACCOUNT_OPEN 或 USER_CREATE（否则开户时报 scene must be ACCOUNT_OPEN or USER_CREATE）；'
    + '外部用户ID 必须与后续开户的 externalUserId 完全一致（不一致按越权拒绝并告警）；'
    + '渠道只收 PDF/PNG/JPG/JPEG 且 ≤10MB，按文件头判定，改扩展名无效。'
    + '拿到的 fileId 填进开户 extParam.attachments[].fileId，fileType 继续用这里选的平台类型名。',
  FV_BANK: 'FV Bank: 本期仅出金附件（scene=WITHDRAW）。上传阶段只落库，真正调 POST /v2/files/upload 的是出金 FSM；documentType 统一映射 customField=Payment_Invoice；渠道侧硬约束 ≤5MB、文件名≤100字符；返回的 fileId(SupportingDocumentID) 供出金 Send Bank Payment 带上。以 externalUserId 定位渠道账户。',
  DLOCAL: 'DLOCAL: 文件先上传至中台存储（channel_file_meta），创建用户时通过 attachments[].fileId 关联提交给渠道。仅支持 PNG/JPEG/PDF。externalUserId 须与创建用户时一致。',
};

const channelHint = (channelCode) => CHANNEL_HINTS[channelCode]
  || `${channelCode}: 上传阶段只落库（中台 S3 + channel_file_meta）。该渠道未在中台登记上传分发器，`
    + '后续流程也不会拿这个 fileId 推给渠道；文件类型用的是通用枚举，渠道侧未必认。';

const SCENES = [
  { value: 'ACCOUNT_OPEN', label: '开户 (ACCOUNT_OPEN)' },
  { value: 'USER_CREATE', label: '用户创建 (USER_CREATE)' },
  { value: 'DEPOSIT', label: '充值 (DEPOSIT)' },
  { value: 'WITHDRAW', label: '提现 (WITHDRAW)' },
  { value: 'CARD_BIND', label: '绑卡 (CARD_BIND)' },
  { value: 'REFUND', label: '退款 (REFUND)' },
];

const DOCUMENT_TYPES_DEFAULT = [
  { group: 'KYC', items: [
    { value: 'ID_CARD', label: '身份证 (ID_CARD)' },
    { value: 'PASSPORT', label: '护照 (PASSPORT)' },
    { value: 'DRIVER_LICENSE', label: '驾照 (DRIVER_LICENSE)' },
    { value: 'ID_CARD_FRONT', label: '身份证正面 (ID_CARD_FRONT)' },
    { value: 'ID_CARD_BACK', label: '身份证背面 (ID_CARD_BACK)' },
    { value: 'SELFIE', label: '自拍照 (SELFIE)' },
  ]},
  { group: 'EDD/AML', items: [
    { value: 'ADDRESS_PROOF', label: '地址证明 (ADDRESS_PROOF)' },
    { value: 'BANK_STATEMENT', label: '银行流水 (BANK_STATEMENT)' },
    { value: 'SOURCE_OF_FUNDS', label: '资金来源 (SOURCE_OF_FUNDS)' },
    { value: 'SOURCE_OF_WEALTH', label: '财富来源 (SOURCE_OF_WEALTH)' },
  ]},
  { group: 'CARD', items: [
    { value: 'CARD_FRONT', label: '银行卡正面 (CARD_FRONT)' },
    { value: 'CARD_BACK', label: '银行卡背面 (CARD_BACK)' },
  ]},
  { group: 'OTHER', items: [
    { value: 'AUTHORIZATION_LETTER', label: '授权书 (AUTHORIZATION_LETTER)' },
  ]},
];

// 变更（2026-06）：Tazapay 建用户实际用附件 fileId（业务方 S3 地址）直传，不走本通用上传接口；
// 此处 documentType 对应渠道 documents.type，取值与建用户 fileType 一致（11 种，代表人文档为其中 8 种子集）。
// 采用单组扁平列表，避免分组间 value 重复（identity/address/other 企业与代表人共用）。
const DOCUMENT_TYPES_TAZAPAY = [
  { group: 'Tazapay 文档类型 (documents.type)', items: [
    { value: 'proof_of_identity', label: 'proof_of_identity' },
    { value: 'proof_of_address', label: 'proof_of_address' },
    { value: 'registration_documents', label: 'registration_documents' },
    { value: 'article_of_incorporation', label: 'article_of_incorporation' },
    { value: 'shareholder_registry', label: 'shareholder_registry' },
    { value: 'identity', label: 'identity' },
    { value: 'business', label: 'business（仅企业）' },
    { value: 'address', label: 'address' },
    { value: 'source_of_wealth', label: 'source_of_wealth（仅企业）' },
    { value: 'person', label: 'person（仅企业）' },
    { value: 'other', label: 'other' },
  ]},
];

const DOCUMENT_TYPES_MDAQ = [
  { group: '公司文档 (Company)', items: [
    { value: 'COMPANY_PROOF', label: '公司证明文件 (COMPANY_PROOF)' },
    { value: 'COMPANY_ADDRESS_PROOF', label: '公司地址证明 (COMPANY_ADDRESS_PROOF)' },
  ]},
  { group: '身份证件 (Identity)', items: [
    { value: 'PASSPORT', label: '护照 (PASSPORT)' },
    { value: 'NATIONAL_IDENTITY', label: '国民身份证 (NATIONAL_IDENTITY)' },
    { value: 'NRIC', label: '新加坡身份证 (NRIC)' },
    { value: 'KTP', label: '印尼身份证 (KTP)' },
    { value: 'WORK_PERMIT', label: '工作许可 (WORK_PERMIT)' },
  ]},
  { group: '地址/账单证明 (Proof)', items: [
    { value: 'ADDRESS_PROOF', label: '地址证明 (ADDRESS_PROOF)' },
    { value: 'UTILITY_BILL', label: '水电账单 (UTILITY_BILL)' },
    { value: 'PHONE_BILL', label: '电话账单 (PHONE_BILL)' },
    { value: 'TAX_BILL', label: '税单 (TAX_BILL)' },
    { value: 'BANK_STATEMENT', label: '银行流水 (BANK_STATEMENT)' },
  ]},
];

// FV Bank：本期仅出金附件（scene=WITHDRAW）。documentType 不区分类型，统一映射渠道 customField=Payment_Invoice；
// 这里给出 WITHDRAW 场景的标准枚举供选择（WithdrawFileRule.supportedDocumentTypes）。
const DOCUMENT_TYPES_FV_BANK = [
  { group: '出金附件 (WITHDRAW)', items: [
    { value: 'SOURCE_OF_FUNDS', label: '资金来源 (SOURCE_OF_FUNDS)' },
    { value: 'SOURCE_OF_WEALTH', label: '财富来源 (SOURCE_OF_WEALTH)' },
    { value: 'BANK_STATEMENT', label: '银行流水 (BANK_STATEMENT)' },
    { value: 'AUTHORIZATION_LETTER', label: '授权书 (AUTHORIZATION_LETTER)' },
  ]},
];

// GOWD：取值是平台统一类型 ChannelFileDocumentType（与开户 extParam.attachments[].fileType 同一套词表），
// 括号内为中台转换后发给渠道的原文。企业与个人的渠道原文不同
// （GowdDocumentTypeEnum 的 ...Representative vs GowdIndividualDocumentTypeEnum 的 ...Holder），
// 由中台按 userType 自行选择，上传时不需要区分。
// ★ 标的是开户必传类型：目前只有个人主体强制（ID_CARD / SELFIE / PEP_DECLARATION），
// 企业主体已放开为「所有附件非必输」（由渠道账户 extra_config.organizationRequiredDocuments 控制），
// 下面企业组里标「建议」的几类不拦提交，但渠道人工审核缺件要等数天，实测仍建议带齐。
const DOCUMENT_TYPES_GOWD = [
  { group: '两类主体通用', items: [
    { value: 'ID_CARD', label: 'ID_CARD ★个人必传 身份证件 (documentRepresentative / documentHolder)' },
    { value: 'SELFIE', label: 'SELFIE ★个人必传 手持证件自拍 (selfieRepresentative / selfieHolder)' },
    { value: 'PEP_DECLARATION', label: 'PEP_DECLARATION ★个人必传 PEP 声明 (pepDeclaration)' },
    { value: 'PASSPORT', label: 'PASSPORT 护照 (→ documentRepresentative / documentHolder)' },
    { value: 'DRIVER_LICENSE', label: 'DRIVER_LICENSE 驾照 (→ 同上)' },
    { value: 'ID_CARD_FRONT', label: 'ID_CARD_FRONT 证件正面 (→ 同上)' },
    { value: 'ID_CARD_BACK', label: 'ID_CARD_BACK 证件背面 (→ 同上)' },
    { value: 'NATIONAL_IDENTITY', label: 'NATIONAL_IDENTITY 国民身份证 (→ 同上)' },
  ]},
  { group: '仅企业主体 (userType=BUSINESS)', items: [
    { value: 'COMPANY_PROOF', label: 'COMPANY_PROOF 建议 公司章程/社会契约 (socialContract)' },
    { value: 'BALANCE_SHEET', label: 'BALANCE_SHEET 建议 资产负债表 (balanceSheet)' },
    { value: 'COMPANY_TAX_ID', label: 'COMPANY_TAX_ID CNPJ 卡 (cnpjCard)' },
    { value: 'ADDRESS_PROOF', label: 'ADDRESS_PROOF 主体地址证明 (proofAddress)' },
    { value: 'BANK_ADDRESS_PROOF', label: 'BANK_ADDRESS_PROOF 银行地址证明 (proofBankAddress)' },
    { value: 'PARTNER_ADDRESS_PROOF', label: 'PARTNER_ADDRESS_PROOF 合伙人地址证明 (partnerProofAddress)' },
    { value: 'TAX_BILL', label: 'TAX_BILL 所得税申报 IR (ir)' },
    { value: 'KYC_QUESTIONNAIRE', label: 'KYC_QUESTIONNAIRE KYC 问卷 (kyc)' },
    { value: 'INCOME_STATEMENT', label: 'INCOME_STATEMENT 收入证明 (incomeStatement)' },
  ]},
  { group: '仅个人主体 (userType=INDIVIDUAL)', items: [
    { value: 'ACCOUNT_TERMS', label: 'ACCOUNT_TERMS 开户条款 (accountTerms)' },
  ]},
];

// DLOCAL：创建用户时通过 attachments[].fileId 关联提交给渠道，仅支持 PNG/JPEG/PDF。
// documentType 为平台统一类型，中台在创建用户流程中映射为渠道侧字段。
const DOCUMENT_TYPES_DLOCAL = [
  { group: '个人 KYC (Individual)', items: [
    { value: 'ID_FRONT', label: '身份证正面 (ID_FRONT)' },
    { value: 'ID_BACK', label: '身份证反面 (ID_BACK)' },
    { value: 'SELFIE', label: '自拍照 (SELFIE)' },
    { value: 'PROOF_OF_ADDRESS', label: '地址证明 (PROOF_OF_ADDRESS)' },
  ]},
  { group: '企业 KYC (Business)', items: [
    { value: 'COMPANY_PROOF', label: '公司证明 (COMPANY_PROOF)' },
    { value: 'LEGAL_REPRESENTATIVE_ID_FRONT', label: '法人代表身份证正面 (LEGAL_REPRESENTATIVE_ID_FRONT)' },
    { value: 'LEGAL_REPRESENTATIVE_ID_BACK', label: '法人代表身份证反面 (LEGAL_REPRESENTATIVE_ID_BACK)' },
    { value: 'UBO_ID_FRONT', label: 'UBO身份证正面 (UBO_ID_FRONT)' },
    { value: 'UBO_ID_BACK', label: 'UBO身份证反面 (UBO_ID_BACK)' },
    { value: 'UBO_PROOF_OF_ADDRESS', label: 'UBO地址证明 (UBO_PROOF_OF_ADDRESS)' },
  ]},
  { group: '财务文档 (Financial)', items: [
    { value: 'BANK_STATEMENT', label: '银行流水 (BANK_STATEMENT)' },
  ]},
];

const DOCUMENT_SIDES = [
  { value: 'FRONT', label: '正面 (FRONT)' },
  { value: 'BACK', label: '背面 (BACK)' },
];

export default function FileUpload() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channels, setChannels] = useState(FALLBACK_CHANNELS);

  useEffect(() => { loadChannels(); }, []);

  /**
   * 渠道列表来源：POST /test/execute { testType: 'CHANNEL_CURRENCIES' }
   *   → TestExecutionService.getChannelCurrenciesForUser（按登录用户 channel/currencies 过滤）
   *   → 中台 GET /v1/fiat/payment/channel/config/channel-currencies
   *   → fiat_channel_config（status=1 且在 effective/expire 有效期内）
   * 与开户/充值/提现页同一个接口、同一份缓存（api.js 里有进程内缓存）。
   * 上传不关心币种，只取 map 的 key。
   */
  const loadChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const map = data.data?.responseBody || data.data || {};
      const list = Object.keys(map);
      if (list.length === 0) return; // 接口通但返回空 → 保留兜底列表
      setChannels([
        ...KNOWN_CHANNELS.filter(c => list.includes(c)),
        ...list.filter(c => !KNOWN_CHANNELS.includes(c)).sort(),
      ]);
    } catch (e) {
      // 拿不到就用兜底列表，不让渠道下拉变成空的
      console.error('Failed to load channels:', e);
    }
  };

  const handleSubmit = async (values) => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }
    setLoading(true);
    try {
      const rand = () => Math.random().toString(36).slice(2, 8);
      const ts = Date.now();
      // bizId / externalUserId 用表单填写的"外部用户ID"，必须与后续创建用户时填写的 externalUserId 完全一致，
      // 否则创建用户阶段按 externalUserId 捞不到本次上传的文件
      const externalUserId = (values.externalUserId || '').trim();
      const formData = new FormData();
      formData.append('channelCode', values.channelCode);
      formData.append('scene', values.scene);
      formData.append('documentType', values.documentType);
      formData.append('bizId', externalUserId);
      formData.append('externalUserId', externalUserId);
      formData.append('requestId', `req_${ts}_${rand()}`);
      if (values.documentCountry) formData.append('documentCountry', values.documentCountry);
      if (values.documentSide) formData.append('documentSide', values.documentSide);
      formData.append('file', fileList[0].originFileObj || fileList[0]);

      const { data } = await metaApi.uploadFile(formData);
      setResult(data);
      if (String(data?.code) === '200' || data?.code === 200) {
        message.success('文件上传成功');
        // 保存上传记录到 localStorage，供创建用户时选择
        const fileId = data?.data?.fileId;
        if (fileId) {
          const uploadedFiles = JSON.parse(localStorage.getItem('tazapay_uploaded_files') || '[]');
          uploadedFiles.unshift({
            fileId,
            documentType: values.documentType,
            fileName: fileList[0]?.name || fileList[0]?.originFileObj?.name || 'unknown',
            originalFileName: fileList[0]?.name || fileList[0]?.originFileObj?.name || 'unknown',
            // S3 object key 格式: {channel}/{scene}/{bizId}/{documentType}/{fileId}
            localFilePath: `${values.channelCode}/${values.scene}/${formData.get('bizId')}/${values.documentType}/${fileId}`,
            channelCode: values.channelCode,
            uploadTime: new Date().toLocaleString(),
          });
          // 最多保留 50 条
          localStorage.setItem('tazapay_uploaded_files', JSON.stringify(uploadedFiles.slice(0, 50)));
        }
      } else {
        message.warning(`上传失败: ${data?.msg || '未知错误'}`);
      }
    } catch (error) {
      console.error('File upload error:', error);
      message.error('上传失败: ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setFileList([]);
    setSelectedChannel(null);
  };

  const uploadProps = {
    beforeUpload: () => false, // prevent auto upload
    fileList,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList.slice(-1)), // single file
    maxCount: 1,
    accept: '.jpg,.jpeg,.png,.pdf,.doc,.docx',
  };

  // Build grouped document type options based on selected channel
  const docTypes = selectedChannel === 'TAZAPAY'
    ? DOCUMENT_TYPES_TAZAPAY
    : selectedChannel === 'MDAQ'
      ? DOCUMENT_TYPES_MDAQ
      : selectedChannel === 'FV_BANK'
        ? DOCUMENT_TYPES_FV_BANK
        : selectedChannel === 'GOWD'
          ? DOCUMENT_TYPES_GOWD
          : selectedChannel === 'DLOCAL'
            ? DOCUMENT_TYPES_DLOCAL
            : DOCUMENT_TYPES_DEFAULT;
  const docTypeOptions = docTypes.map(group => ({
    label: group.group,
    options: group.items,
  }));

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px', borderRadius: 8, marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <CloudUploadOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>渠道文件上传</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} type="text" style={{ color: '#fff' }}>重置</Button>
        </div>

        <Row gutter={16}>
          {/* Left: Form */}
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: 24 }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit}
                initialValues={{ externalUserId: localStorage.getItem('uid') || localStorage.getItem('username') || '' }}>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>📋 基础信息</div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="渠道" name="channelCode" rules={[{ required: true, message: '请选择渠道' }]}>
                      <Select placeholder="选择渠道" onChange={v => {
                          setSelectedChannel(v);
                          form.setFieldValue('documentType', undefined);
                          // 渠道限定了 scene 就直接预置第一个，避免选了非法场景、到开户/出金时才被拒
                          const allowed = CHANNEL_SCENES[v];
                          if (allowed) form.setFieldValue('scene', allowed[0]);
                        }}
                        showSearch
                        optionFilterProp="title"
                        options={channels.map(c => {
                          const logo = getChannelLogo(c);
                          return {
                            title: c,
                            value: c,
                            // 不再按渠道打「是否推渠道」的标：本接口对所有渠道都只是落库，没有差别。
                            // 只给中台未登记文件处理的渠道留一个提示，避免误以为传了就有用。
                            label: (
                              <Space>
                                <span style={{ fontSize: 16 }}>{logo.emoji}</span>
                                <span>{logo.name}</span>
                                {!KNOWN_CHANNELS.includes(c) && <Tag style={{ marginInlineEnd: 0 }}>未接入</Tag>}
                              </Space>
                            ),
                          };
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="业务场景" name="scene" rules={[{ required: true, message: '请选择场景' }]}>
                      <Select
                        placeholder="选择场景"
                        disabled={(CHANNEL_SCENES[selectedChannel] || []).length === 1}
                        options={CHANNEL_SCENES[selectedChannel]
                          ? SCENES.filter(s => CHANNEL_SCENES[selectedChannel].includes(s.value))
                          : SCENES}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="文件类型" name="documentType" rules={[{ required: true, message: '请选择文件类型' }]}>
                      <Select placeholder="选择文件类型" options={docTypeOptions} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="文件面" name="documentSide">
                      <Select placeholder="选择（可选）" allowClear options={DOCUMENT_SIDES} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>🔗 业务关联</div>

                <Alert
                  type="warning" showIcon style={{ marginBottom: 16, fontSize: 12 }}
                  message="外部用户ID 必须与创建用户时填写的一致"
                  description="文件按「外部用户ID」归属。上传时填的值会作为 bizId 存储，创建用户阶段再按相同的外部用户ID 捞取文件。两处不一致会导致创建用户时找不到已上传的文件。默认填充当前登录账号，可按需修改。"
                />

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="外部用户ID (externalUserId)" name="externalUserId"
                      tooltip="与创建用户时填写的 externalUserId 保持一致"
                      rules={[{ required: true, message: '请输入外部用户ID' }]}>
                      <Input placeholder="例如: mdaq_kyb_1780904457" style={{ fontFamily: 'monospace' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="证件国家" name="documentCountry" tooltip="如 SG / HK / US">
                      <Input placeholder="可选，例如: SG" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>📎 文件选择</div>

                <Form.Item>
                  <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: '#667eea', fontSize: 40 }} /></p>
                    <p className="ant-upload-text" style={{ fontSize: 14 }}>点击或拖拽文件到此区域</p>
                    <p className="ant-upload-hint" style={{ fontSize: 12, color: '#9ca3af' }}>
                      支持 JPG / PNG / PDF / DOC / DOCX，单个文件
                    </p>
                  </Dragger>
                </Form.Item>

                {selectedChannel && (
                  <Alert
                    type={KNOWN_CHANNELS.includes(selectedChannel) ? 'info' : 'warning'}
                    showIcon style={{ marginBottom: 16, fontSize: 12 }}
                    message={channelHint(selectedChannel)}
                  />
                )}

                <Button type="primary" htmlType="submit" icon={<CloudUploadOutlined />}
                  loading={loading} size="large" block className="primary-button"
                  style={{ height: 48 }} disabled={fileList.length === 0}>
                  {loading ? '上传中...' : '上传文件'}
                </Button>
              </Form>
            </Card>
          </Col>

          {/* Right: Result */}
          <Col xs={24} lg={12}>
            <Card className="modern-card" title="📋 上传结果" bodyStyle={{ padding: 24 }}>
              {result ? (
                <div className="json-viewer">
                  <ReactJson src={result} collapsed={false} theme="rjv-default"
                    displayDataTypes={false} displayObjectSize={false} enableClipboard />
                </div>
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f9fafb',
                  borderRadius: 8, border: '2px dashed #d1d5db' }}>
                  <FileOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>提交表单后将显示上传结果</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
